// src/components/OrgManagementPage.js

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding, faTrashAlt, faSpinner, faCheckCircle,
  faExclamationTriangle, faTimesCircle, faShieldAlt, faKeyboard,
  faUserSlash, faUsersCog, faImages, faCalendarCheck // Added faCalendarCheck
} from '@fortawesome/free-solid-svg-icons';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Modal, Button, Form, InputGroup, ListGroup } from 'react-bootstrap'; // Added ListGroup

import { functions } from '../firebase';
import { Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';

// --- JSDoc Type Definitions ---
/**
 * @typedef {object} OrganizationData
 * @property {string} id
 * @property {string} [orgName]
 * @property {any} [cDate]
 * @property {string} [contactEmail]
 * @property {string} [contactName]
 * @property {any} [expire]
 * @property {number} [lat]
 * @property {number} [lng]
 * @property {number} [maxMembers]
 * @property {string} [orgAddress]
 */

/** @typedef {object} GetAllOrganizationsResult
 * @property {boolean} success
 * @property {OrganizationData[]} organizations
 * @property {string} [message]
 */
/** @typedef {object} OrgDeletionSummary ... */
/** @typedef {object} DeleteOrgDataResult ... */
/** @typedef {object} OrphanedMemberDeletionSummary ... */
/** @typedef {object} DeleteOrphanedMembersResult ... */
/** @typedef {object} BatchSyncSummary ... */
/** @typedef {object} BatchSyncResult ... */

/**
 * @typedef {object} BackfillSummaryDetail
 * @property {string} [file]
 * @property {string} [newPath]
 * @property {string} status
 * @property {string} [error]
 * @property {any} [details]
 */

/**
 * @typedef {object} BackfillSummary
 * @property {number} orgsScanned
 * @property {number} familiesScanned
 * @property {number} photosChecked
 * @property {number} photosCopiedToStandardPath
 * @property {number} firestorePathsUpdated
 * @property {number} errors
 * @property {BackfillSummaryDetail[]} details
 */

/**
 * @typedef {object} BackfillFaceProcessingResult
 * @property {string} message
 * @property {BackfillSummary} summary
 */

/**
 * @typedef {object} BirthdayIssueDetail
 * @property {string} documentId
 * @property {string} reason
 * @property {string} [details]
 */

/**
 * @typedef {object} BirthdayCleanupReport
 * @property {boolean} success
 * @property {string} message
 * @property {string} orgId
 * @property {number} documentsProcessed
 * @property {number} documentsUpdated
 * @property {number} issuesFound
 * @property {BirthdayIssueDetail[]} issues
 */


// Callable function references
const getAllOrganizationsCallable = httpsCallable(functions, 'getAllOrganizations');
const deleteAllDataCallable = httpsCallable(functions, 'deleteAllDataForOrganization');
const deleteOrphanedMembersCallable = httpsCallable(functions, 'deleteOrphanedMembersByFamily');
const batchSyncClaimsAndUsersCallable = httpsCallable(functions, 'batchSyncClaimsAndFirestoreUsers');
const backfillFaceProcessingCallable = httpsCallable(functions, 'backfillFaceProcessing');
const cleanUpBirthdayDataCallable = httpsCallable(functions, 'cleanUpBirthdayDataForOrg'); // Added

const AUTHORIZED_EMAIL = "jonstewart24@gmail.com";

function OrgManagementPage() {
  const { currentUser, loadingAuth } = useAuth();

  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [actionError, setActionError] = useState(null);
  const [userMessage, setUserMessage] = useState({ type: '', text: '', details: null });

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const [confirmInput, setConfirmInput] = useState("");
  const [isDeletingOrg, setIsDeletingOrg] = useState(false);

  const [manualOrgIdInput, setManualOrgIdInput] = useState("");

  const [isCleaningMembers, setIsCleaningMembers] = useState(false);
  const [cleaningOrgId, setCleaningOrgId] = useState(null);

  const [isBatchSyncing, setIsBatchSyncing] = useState(false);

  const [isBackfillingFaces, setIsBackfillingFaces] = useState(false);
  const [backfillingOrgId, setBackfillingOrgId] = useState(null);

  const [isCleaningBirthdays, setIsCleaningBirthdays] = useState(false); // Added
  const [cleaningBirthdaysOrgId, setCleaningBirthdaysOrgId] = useState(null); // Added

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    try {
      let date;
      if (dateValue instanceof Timestamp) {
        date = dateValue.toDate();
      } else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (dateValue && typeof dateValue._seconds === 'number' && typeof dateValue._nanoseconds === 'number') {
        date = new Timestamp(dateValue._seconds, dateValue._nanoseconds).toDate();
      } else {
        date = new Date(dateValue);
      }
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (e) {
      console.error("Error formatting date:", e, "Value:", dateValue);
      return 'Invalid Date';
    }
  };

  const fetchOrganizations = useCallback(async () => {
    if (currentUser?.email?.toLowerCase() !== AUTHORIZED_EMAIL.toLowerCase()) {
      setLoadingOrgs(false);
      setActionError("Unauthorized to fetch organizations.");
      return;
    }
    setLoadingOrgs(true);
    setActionError(null);
    setUserMessage({ type: '', text: '', details: null });

    try {
      const result = await getAllOrganizationsCallable();
      /** @type {GetAllOrganizationsResult} */
      const data = result.data;
      if (data && data.success) {
        setOrganizations(data.organizations || []);
      } else {
        throw new Error(data?.message || "Failed to fetch organizations from the server.");
      }
    } catch (err) {
      console.error("OrgManagementPage: Error calling getAllOrganizations function:", err);
      let displayError = "An unknown error occurred while fetching organizations.";
        if (err.code && err.message) {
        displayError = `Error (${err.code}): ${err.message}`;
      } else if (err.message) {
        displayError = err.message;
      }
      setActionError(displayError);
      setOrganizations([]);
    } finally {
      setLoadingOrgs(false);
    }
  }, [currentUser?.email]);

  useEffect(() => {
    if (!loadingAuth && currentUser) {
      fetchOrganizations();
    } else if (!loadingAuth && !currentUser) {
      setLoadingOrgs(false);
      setActionError("User not authenticated.");
    }
  }, [loadingAuth, currentUser, fetchOrganizations]);

  const handleInitiateDelete = (org) => {
    setOrgToDelete({
        id: org.id,
        orgName: org.orgName || `Org ID: ${org.id}`
    });
    setConfirmInput("");
    setShowConfirmModal(true);
    setUserMessage({ type: '', text: '', details: null });
    setActionError(null);
  };

  const handleInitiateManualDelete = () => {
    if (!manualOrgIdInput.trim()) {
        setUserMessage({ type: 'warning', text: 'Please enter an Organization ID for manual deletion.' });
        return;
    }
    handleInitiateDelete({ id: manualOrgIdInput.trim(), orgName: `Org ID: ${manualOrgIdInput.trim()}` });
  };


  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
    setOrgToDelete(null);
    setConfirmInput("");
  };

  const handleConfirmDelete = async () => {
    if (!orgToDelete || !orgToDelete.id || confirmInput !== orgToDelete.id) {
      setUserMessage({ type: 'warning', text: 'Confirmation text does not match Organization ID. Deletion cancelled.' });
      return;
    }

    setIsDeletingOrg(true);
    setUserMessage({ type: '', text: '', details: null });
    setActionError(null);

    try {
      const result = await deleteAllDataCallable({ orgId: orgToDelete.id });
      /** @type {DeleteOrgDataResult} */
      const data = result.data;

      if (data.success) {
        setUserMessage({
          type: 'success',
          text: `Successfully deleted all data for organization: ${orgToDelete.orgName || orgToDelete.id}. Summary: ${JSON.stringify(data.deletedCounts)}`
        });
        setManualOrgIdInput("");
        fetchOrganizations();
      } else {
        throw new Error(data.message || `Failed to delete data for ${orgToDelete.orgName || orgToDelete.id}.`);
      }
    } catch (err) {
      console.error(`Error deleting data for org ${orgToDelete.id}:`, err);
      let displayError = "An unknown error occurred during deletion.";
        if (err.code && err.message) {
        displayError = `Error (${err.code}): ${err.message}`;
      } else if (err.message) {
        displayError = err.message;
      }
      setUserMessage({ type: 'danger', text: `Deletion failed: ${displayError}` });
    } finally {
      setIsDeletingOrg(false);
      handleCloseConfirmModal();
    }
  };

  const handleCleanOrphanedMembers = async (orgId, orgName) => {
    const confirmCleanup = window.confirm(
      `Are you sure you want to clean up orphaned members for organization: ${orgName || orgId} (ID: ${orgId})?\n\nThis will delete members whose familyId points to a non-existent family within this organization.`
    );

    if (!confirmCleanup) return;

    setIsCleaningMembers(true);
    setCleaningOrgId(orgId);
    setUserMessage({ type: '', text: '', details: null });
    setActionError(null);

    try {
      const result = await deleteOrphanedMembersCallable({ orgId: orgId });
      /** @type {DeleteOrphanedMembersResult} */
      const data = result.data;

      if (data.success) {
        setUserMessage({
          type: 'success',
          text: `Orphaned member cleanup for ${orgName || orgId} successful. ${data.summary.orphanedMembersDeleted} members deleted.`
        });
      } else {
        throw new Error(data.message || `Failed to clean orphaned members for ${orgName || orgId}.`);
      }
    } catch (err) {
      console.error(`Error cleaning orphaned members for org ${orgId}:`, err);
      let displayError = "An unknown error occurred during member cleanup.";
        if (err.code && err.message) {
        displayError = `Error (${err.code}): ${err.message}`;
      } else if (err.message) {
        displayError = err.message;
      }
      setUserMessage({ type: 'danger', text: `Member cleanup failed: ${displayError}` });
    } finally {
      setIsCleaningMembers(false);
      setCleaningOrgId(null);
    }
  };

  const handleBatchSyncClaimsAndUsers = async () => {
    const confirmSync = window.confirm(
      "Are you sure you want to run the batch process to sync Auth claims and Firestore 'users' records for ALL users?\n\nThis operation may take several minutes and will update Auth custom claims and create/update documents in the 'users' Firestore collection."
    );

    if (!confirmSync) return;

    setIsBatchSyncing(true);
    setUserMessage({ type: '', text: '', details: null });
    setActionError(null);

    try {
      const result = await batchSyncClaimsAndUsersCallable();
      /** @type {BatchSyncResult} */
      const data = result.data;

      if (data.success) {
        setUserMessage({
          type: 'success',
          text: `Batch sync successful! ${data.message} Summary: ${JSON.stringify(data.summary, null, 2)}`
        });
      } else {
        throw new Error(data.message || "Batch sync function reported a failure.");
      }
    } catch (err) {
      console.error("Error calling batchSyncClaimsAndFirestoreUsers function:", err);
      let displayError = "An unknown error occurred during the batch sync process.";
      if (err.code && err.message) {
        displayError = `Error (${err.code}): ${err.message}`;
      } else if (err.message) {
        displayError = err.message;
      }
      setUserMessage({ type: 'danger', text: `Batch sync failed: ${displayError}` });
    } finally {
      setIsBatchSyncing(false);
    }
  };

  const handleRunBackfillForOrg = async (orgId, orgName, dryRun = false) => {
    const actionType = dryRun ? "DRY RUN for photo standardization & face backfill" : "Photo Standardization & Face Backfill";
    const confirmMessage = `Are you sure you want to run ${actionType} for organization: ${orgName || 'N/A'} (ID: ${orgId})?\n\nThis will check family photos, copy them to a standard path if needed (triggering face extraction), and update Firestore.`;

    if (!window.confirm(confirmMessage)) return;

    setIsBackfillingFaces(true);
    setBackfillingOrgId(orgId);
    setUserMessage({ type: '', text: '', details: null });
    setActionError(null);

    try {
      const payload = { orgId, dryRun };
      const result = await backfillFaceProcessingCallable(payload);
      /** @type {BackfillFaceProcessingResult} */
      const data = result.data;

      if (data && data.message) {
        setUserMessage({
          type: data.summary.errors > 0 ? 'warning' : 'success',
          text: `[Org: ${orgId}] ${data.message} Summary: ${JSON.stringify(data.summary, null, 2)}`
        });
      } else {
        throw new Error("Received an unexpected response from the backfill function.");
      }
    } catch (err) {
      console.error(`Error running backfill for org ${orgId}:`, err);
      let displayError = `[Org ID: ${orgId}] An unknown error occurred during face backfill.`;
      if (err.code && err.message) {
        displayError = `[Org ID: ${orgId}] Error (${err.code}): ${err.message}`;
        if (err.details) displayError += ` Details: ${JSON.stringify(err.details)}`;
      } else if (err.message) {
        displayError = err.message;
      }
      setUserMessage({ type: 'danger', text: `Face backfill failed: ${displayError}` });
    } finally {
      setIsBackfillingFaces(false);
      setBackfillingOrgId(null);
    }
  };

  // Handler for cleaning birthday data
  const handleCleanUpBirthdays = async (orgId, orgName) => {
    const confirmAction = window.confirm(
      `Are you sure you want to clean up birthday data for organization: ${orgName || orgId} (ID: ${orgId})?\n\nThis will process 'member' records to standardize birthday fields and remove 'birthdayString'. It will log discrepancies for month/day if existing values differ from the timestamp and will not override them.`
    );

    if (!confirmAction) return;

    setIsCleaningBirthdays(true);
    setCleaningBirthdaysOrgId(orgId);
    setUserMessage({ type: '', text: '', details: null });
    setActionError(null);

    try {
      const result = await cleanUpBirthdayDataCallable({ orgId });
      /** @type {BirthdayCleanupReport} */
      const data = result.data;

      if (data.success) {
        let messageText = `[Org: ${orgId}] ${data.message}`;
        if (data.issuesFound > 0) {
          messageText += ` \nReview issues for details.`;
        }
        setUserMessage({
          type: data.issuesFound > 0 ? 'warning' : 'success',
          text: messageText,
          details: data.issues // Store issues for potential display
        });
      } else {
        throw new Error(data.message || `Failed to clean up birthday data for ${orgName || orgId}.`);
      }
    } catch (err) {
      console.error(`Error cleaning birthday data for org ${orgId}:`, err);
      let displayError = `[Org ID: ${orgId}] An unknown error occurred during birthday data cleanup.`;
      if (err.code && err.message) {
        displayError = `[Org ID: ${orgId}] Error (${err.code}): ${err.message}`;
        if (err.details) displayError += ` Details: ${JSON.stringify(err.details)}`;
      } else if (err.message) {
        displayError = err.message;
      }
      setUserMessage({ type: 'danger', text: `Birthday cleanup failed: ${displayError}` });
    } finally {
      setIsCleaningBirthdays(false);
      setCleaningBirthdaysOrgId(null);
    }
  };

  // --- Render Logic ---
  if (loadingAuth) {
    return (
      <div className="container text-center mt-5">
        <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Verifying access...</span></div>
        <p className="mt-2">Verifying access...</p>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (currentUser.email?.toLowerCase() !== AUTHORIZED_EMAIL.toLowerCase()) {
    return (
      <div className="container mt-3">
        <nav aria-label="breadcrumb" className="mb-3"><ol className="breadcrumb bg-light px-2 py-1 rounded-1 small"><li className="breadcrumb-item">Admin</li><li className="breadcrumb-item active" aria-current="page">Access Denied</li></ol></nav>
        <div className="alert alert-danger" role="alert"><h4><FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />Access Denied</h4><p>You do not have permission to view this page.</p></div>
        <Link to="/dashboard" className="btn btn-secondary">Go to Dashboard</Link>
      </div>
    );
  }

  const isAnyMajorActionRunning = isDeletingOrg || isCleaningMembers || isBatchSyncing || isBackfillingFaces || isCleaningBirthdays;


  return (
    <div className="container-fluid mt-3">
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb bg-light px-2 py-1 rounded-1 small">
          <li className="breadcrumb-item"><Link to="/admin">Admin</Link></li>
          <li className="breadcrumb-item active" aria-current="page">Organization Management & Utilities</li>
        </ol>
      </nav>
      <h2 className="mb-4 text-start">
        <FontAwesomeIcon icon={faShieldAlt} className="me-2" />Organization Management & Utilities
      </h2>

      {userMessage.text && (
        <div className={`alert alert-${userMessage.type || 'info'} alert-dismissible fade show mx-1 mb-3`} role="alert">
          {userMessage.type === 'success' && <FontAwesomeIcon icon={faCheckCircle} className="me-2" />}
          {(userMessage.type === 'danger' || userMessage.type === 'warning') && <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />}
          <span style={{ whiteSpace: 'pre-wrap' }}>{userMessage.text}</span>
          {userMessage.details && userMessage.details.length > 0 && (
            <div className="mt-2">
              <strong>Issues:</strong>
              <ListGroup style={{maxHeight: '200px', overflowY: 'auto'}}>
                {userMessage.details.map((issue, index) => (
                  <ListGroup.Item key={index} variant="light" className="small py-1">
                    <strong>Doc ID:</strong> {issue.documentId} <br />
                    <strong>Reason:</strong> {issue.reason} <br />
                    {issue.details && <><strong>Details:</strong> {issue.details}</>}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}
          <button type="button" className="btn-close" onClick={() => setUserMessage({ type: '', text: '', details: null })} aria-label="Close"></button>
        </div>
      )}
      {actionError && !userMessage.text && (
        <div className="alert alert-danger mx-1 mb-3" role="alert">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" /> {actionError}
        </div>
      )}

      <div className="card shadow-sm mb-4">
        <div className="card-header">
            <h5 className="mb-0"><FontAwesomeIcon icon={faUsersCog} className="me-2" />Batch Sync Auth Claims & Firestore Users</h5>
        </div>
        <div className="card-body">
            <p className="card-text small">
                This utility iterates through all Firebase Authentication users.
                If an email-verified user is missing <code>orgId</code>, <code>userMid</code>, or <code>userFid</code> claims,
                it attempts to find a matching 'member' record by email.
                If found, it sets these claims and <code>admin: "false"</code> (if no admin claim exists).
                It also creates or updates a corresponding document in the 'users' Firestore collection
                with Auth user data and relevant claims. <strong>This is a system-wide operation and may take several minutes.</strong>
            </p>
            <Button
                variant="warning"
                onClick={handleBatchSyncClaimsAndUsers}
                disabled={isAnyMajorActionRunning}
            >
                {isBatchSyncing ? (
                    <><FontAwesomeIcon icon={faSpinner} spin className="me-1" /> Syncing Users & Claims...</>
                ) : (
                    <><FontAwesomeIcon icon={faUsersCog} className="me-1" /> Run Batch Sync</>
                )}
            </Button>
        </div>
      </div>

      <div className="card shadow-sm mb-4">
        <div className="card-header">
            <h5 className="mb-0"><FontAwesomeIcon icon={faKeyboard} className="me-2" />Manual Organization Data Deletion</h5>
        </div>
        <div className="card-body">
            <p className="card-text small">
                Use this section to delete all data for an organization by manually entering its ID.
                This is useful if the organization record itself is already deleted or not listed below.
                <strong>Use with extreme caution.</strong>
            </p>
            <InputGroup className="mb-3">
                <Form.Control
                    type="text"
                    placeholder="Enter Organization ID to delete"
                    value={manualOrgIdInput}
                    onChange={(e) => setManualOrgIdInput(e.target.value)}
                    aria-label="Organization ID for manual deletion"
                />
                <Button
                    variant="outline-danger"
                    onClick={handleInitiateManualDelete}
                    disabled={!manualOrgIdInput.trim() || isAnyMajorActionRunning}
                >
                    <FontAwesomeIcon icon={faTrashAlt} className="me-1" /> Delete Data for this ID
                </Button>
            </InputGroup>
        </div>
      </div>

      {loadingOrgs ? (
        <div className="text-center mt-4 card shadow-sm">
          <div className="card-body">
            <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary mb-2" />
            <p className="mb-0">Loading organizations...</p>
          </div>
        </div>
      ) : organizations.length === 0 && !actionError ? (
        <div className="alert alert-info text-center">No organizations found or you might not be authorized to view them.</div>
      ) : organizations.length > 0 && (
        <div className="card shadow-sm">
          <div className="card-header bg-light">
            <h5 className="mb-0">Organizations List ({organizations.length})</h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped table-hover table-sm align-middle small">
                <thead className="table-light">
                  <tr>
                    <th scope="col">Org Name</th>
                    <th scope="col">Org ID</th>
                    <th scope="col">Created At</th>
                    <th scope="col">Contact Name</th>
                    <th scope="col">Contact Email</th>
                    <th scope="col">Expires At</th>
                    <th scope="col" className="text-center" style={{minWidth: '480px'}}>Actions</th> {/* Increased minWidth for new button */}
                  </tr>
                </thead>
                <tbody>
                  {organizations.map((org) => (
                    <tr key={org.id}>
                      <td>{org.orgName || 'N/A'}</td>
                      <td style={{ wordBreak: 'break-all' }}>{org.id}</td>
                      <td>{formatDate(org.cDate)}</td>
                      <td>{org.contactName || '-'}</td>
                      <td style={{ wordBreak: 'break-all' }}>{org.contactEmail || '-'}</td>
                      <td>{formatDate(org.expire)}</td>
                      <td className="text-center">
                        <Button
                          variant="outline-secondary" // Changed variant for distinction
                          size="sm"
                          className="py-0 px-2 me-1 mb-1" // Added mb-1 for spacing if buttons wrap
                          title={`Clean & Standardize Birthday Data for ${org.orgName || org.id}`}
                          onClick={() => handleCleanUpBirthdays(org.id, org.orgName)}
                          disabled={(isCleaningBirthdays && cleaningBirthdaysOrgId === org.id) || isAnyMajorActionRunning}
                        >
                          {(isCleaningBirthdays && cleaningBirthdaysOrgId === org.id) ? (
                            <FontAwesomeIcon icon={faSpinner} spin fixedWidth />
                          ) : (
                            <FontAwesomeIcon icon={faCalendarCheck} fixedWidth />
                          )}
                          <span className="ms-1 d-none d-lg-inline">Clean Birthdays</span> {/* d-lg-inline for larger screens */}
                        </Button>

                        <Button
                          variant="outline-info"
                          size="sm"
                          className="py-0 px-2 me-1 mb-1"
                          title={`Clean orphaned members for ${org.orgName || org.id}`}
                          onClick={() => handleCleanOrphanedMembers(org.id, org.orgName)}
                          disabled={(isCleaningMembers && cleaningOrgId === org.id) || isAnyMajorActionRunning }
                        >
                          {(isCleaningMembers && cleaningOrgId === org.id) ? (
                            <FontAwesomeIcon icon={faSpinner} spin fixedWidth />
                          ) : (
                            <FontAwesomeIcon icon={faUserSlash} fixedWidth />
                          )}
                          <span className="ms-1 d-none d-lg-inline">Clean Members</span>
                        </Button>

                        <Button
                            variant="outline-success"
                            size="sm"
                            className="py-0 px-2 me-1 mb-1"
                            title={`Standardize photos & backfill face detection for ${org.orgName || org.id}`}
                            onClick={() => handleRunBackfillForOrg(org.id, org.orgName, false)}
                            disabled={
                                (isBackfillingFaces && backfillingOrgId === org.id) ||
                                (isBackfillingFaces && backfillingOrgId !== null && backfillingOrgId !== org.id) || // Prevent multiple backfills
                                isAnyMajorActionRunning
                            }
                        >
                            {(isBackfillingFaces && backfillingOrgId === org.id) ? (
                                <FontAwesomeIcon icon={faSpinner} spin fixedWidth />
                            ) : (
                                <FontAwesomeIcon icon={faImages} fixedWidth />
                            )}
                            <span className="ms-1 d-none d-lg-inline">Faces Backfill</span>
                        </Button>

                        <Button
                          variant="danger"
                          size="sm"
                          className="py-0 px-2 mb-1"
                          title={`Delete all data for ${org.orgName || org.id}`}
                          onClick={() => handleInitiateDelete(org)}
                          disabled={(isDeletingOrg && orgToDelete?.id === org.id) || isAnyMajorActionRunning}
                        >
                          {(isDeletingOrg && orgToDelete?.id === org.id) ? (
                            <FontAwesomeIcon icon={faSpinner} spin fixedWidth />
                          ) : (
                            <FontAwesomeIcon icon={faTrashAlt} fixedWidth />
                          )}
                          <span className="ms-1 d-none d-lg-inline">Delete Org</span>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {orgToDelete && (
        <Modal show={showConfirmModal} onHide={handleCloseConfirmModal} centered backdrop="static">
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger me-2" />
              Confirm Destructive Action
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>You are about to delete <strong>ALL</strong> associated data for the organization:</p>
            <p><strong>Name/Identifier:</strong> {orgToDelete.orgName || `ID: ${orgToDelete.id}`}</p>
            {orgToDelete.orgName && orgToDelete.orgName !== `Org ID: ${orgToDelete.id}` && <p><strong>ID:</strong> <code>{orgToDelete.id}</code></p> }
            <p className="text-danger"><strong>This action is irreversible and will permanently remove all families, members, groups, roles, and users associated with this organization ID from the database.</strong></p>
            <hr />
            <Form.Group>
              <Form.Label>To confirm, please type the Organization ID (<code>{orgToDelete.id}</code>) below:</Form.Label>
              <Form.Control
                type="text"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                placeholder="Type Organization ID here"
                autoFocus
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseConfirmModal} disabled={isDeletingOrg}>
              <FontAwesomeIcon icon={faTimesCircle} className="me-1" /> Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmDelete}
              disabled={isDeletingOrg || !orgToDelete || confirmInput !== orgToDelete.id}
            >
              {isDeletingOrg ? (
                <><FontAwesomeIcon icon={faSpinner} spin className="me-1" /> Deleting Org Data...</>
              ) : (
                <><FontAwesomeIcon icon={faTrashAlt} className="me-1" /> Confirm & Delete All Data</>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}

export default OrgManagementPage;